import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Alert, Box, Button, CircularProgress, Collapse, Stack, Typography } from "@mui/material";
import { ArrowBack, Download, History, PlayArrow } from "@mui/icons-material";

import api from "services/api";
import { downloadHtmlReport } from "v2/pages/Insights/downloadHtmlReport";
import { IContract } from "../ListContracts/ListContracts.types";
import { workspaceLabelOf } from "./evidenceFormat";
import { diffStatusMap, EMPTY_FILTERS, evidenceChangeMap, groupBySection, IFilters } from "./groupFindings";
import { fetchInspectionReport, getInspection, listInspections, runInspection } from "./raio-x.service";
import { ITarget } from "./raio-x.types";
import { RaioXHistory } from "./RaioXHistory";
import { RaioXSections } from "./RaioXSections";
import { RaioXSummary } from "./RaioXSummary";
import { RaioXWorkspaceDrawer } from "./RaioXWorkspaceDrawer";

const FORBIDDEN_FALLBACK = "Sua organização não é corretora deste CNPJ.";

// why: authorize-seller.ts devolve 403 com { name, message } quando a organização não é corretora deste CNPJ; distinguir isso de erro de rede ou 500 evita um aviso genérico que não diz o que fazer.
function forbiddenMessage(error: unknown): string | null {
  if (isAxiosError(error) && error.response?.status === 403) {
    const message = (error.response.data as { message?: string } | undefined)?.message;
    return message ?? FORBIDDEN_FALLBACK;
  }
  return null;
}

// why: outros 4xx (ex.: 404 "Esta corretora não tem contrato configurado com X.") também trazem message legível do backend; sem isso o aviso genérico escondia o motivo real de rodar ter falhado.
function apiMessage(error: unknown): string | null {
  if (isAxiosError(error) && error.response && error.response.status >= 400 && error.response.status < 500) {
    return (error.response.data as { message?: string } | undefined)?.message ?? null;
  }
  return null;
}

export function RaioX({ organizationId, onBack }: { organizationId: string; onBack: () => void }) {
  const queryClient = useQueryClient();
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [filters, setFilters] = useState<IFilters>(EMPTY_FILTERS);
  const [openWorkspace, setOpenWorkspace] = useState<ITarget | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportNotice, setReportNotice] = useState<string | null>(null);

  const history = useQuery({ queryKey: ["raio-x", "list", organizationId], queryFn: () => listInspections(organizationId), staleTime: 60_000 });
  const forbidden = forbiddenMessage(history.error);
  const effectiveId = currentId ?? history.data?.[0]?.id ?? null;
  const latestId = history.data?.[0]?.id ?? null;
  const viewingOlder = Boolean(effectiveId && latestId && effectiveId !== latestId);
  const inspection = useQuery({ queryKey: ["raio-x", "inspection", effectiveId], queryFn: () => getInspection(effectiveId as string), enabled: Boolean(effectiveId), staleTime: 60_000 });
  // why: mesma chave que ListContracts.tsx usa para /workspaces; reaproveita o cache do React Query em vez de duplicar a chamada.
  const contracts = useQuery({ queryKey: ["contracts", organizationId], queryFn: () => api.get<{ data: IContract[] }>(`/v2/organizations/${organizationId}/workspaces`), select: (d) => d.data.data, staleTime: 60_000 });

  // why: uma razão social com espaço à direita ("METLIFE SEGUROS SA ") deixava espaço duplo antes do
  // separador " · "; cada parte passa por trim() e a parte vazia depois do trim sai (mesma fórmula de
  // load-buyer-report-info.ts no backend). hazard: sem fornecedora, produto e número, as três partes
  // saem no filter(Boolean) e o rótulo vira "" (string vazia); "" é chave própria de workspaceLabels,
  // então workspaceLabelOf devolveria "" (o ?? só cai no fallback para null/undefined, não para "").
  // Por isso a chave só entra no mapa quando o rótulo não é vazio, como load-buyer-report-info.ts faz
  // (`if (label) workspaceLabels[ws.id] = label`), e workspaceLabelOf cai no fallback do id curto.
  const workspaceLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    (contracts.data ?? []).forEach((c) => {
      const label = [c.product?.supplierOrganization?.corporateName, c.product?.name, c.policyContractNumber ? `Contrato ${c.policyContractNumber}` : null]
        .map((s) => (typeof s === "string" ? s.trim() : s))
        .filter(Boolean)
        .join(" · ");
      if (label) labels[c.id] = label;
    });
    return labels;
  }, [contracts.data]);

  const run = useMutation({
    mutationFn: () => runInspection(organizationId),
    onMutate: () => setRunError(null),
    onSuccess: async ({ id }) => {
      await queryClient.invalidateQueries({ queryKey: ["raio-x", "list", organizationId] });
      setCurrentId(id);
      setFilters(EMPTY_FILTERS);
    },
    // why: 403 e outros 4xx (ex.: 404 sem contrato) trazem mensagem legível do backend; "a inspeção anterior continua abaixo" só faz sentido quando há uma inspeção efetivamente na tela (effectiveId).
    onError: (error) => {
      const backendMessage = forbiddenMessage(error) ?? apiMessage(error);
      const fallback = effectiveId ? "Não foi possível rodar a inspeção agora. A inspeção anterior continua abaixo." : "Não foi possível rodar a inspeção agora.";
      setRunError(backendMessage ?? fallback);
    },
  });

  const report = useMutation({
    mutationFn: () => fetchInspectionReport([organizationId]),
    onMutate: () => {
      setReportError(null);
      setReportNotice(null);
    },
    onSuccess: async (r) => {
      await downloadHtmlReport(r.html, r.filename);
      // why: entre o topo do histórico e o clique, alguém pode ter rodado outra inspeção, ou o
      // relatório usa a última DONE enquanto a lista não filtra por status; o id pode divergir do
      // topo sem que a de agora seja necessariamente "mais nova" (hazard: dizer "mais nova" seria
      // falso nesse segundo caso). O texto fica neutro: só que é diferente da que está no topo.
      if (r.chapters[0]?.inspectionId && r.chapters[0].inspectionId !== latestId) {
        setReportNotice("O relatório saiu de uma inspeção diferente da que aparece no topo da lista. O histórico foi atualizado.");
        await queryClient.invalidateQueries({ queryKey: ["raio-x", "list", organizationId] });
      }
    },
    // why: forbiddenMessage antes de apiMessage, como em "Rodar", para o 403 sem corpo não cair no texto
    // padrão; uma falha dentro de downloadHtmlReport (ex.: fetch do logo) também cai aqui, e o arquivo
    // não foi baixado, então não dizemos que foi.
    onError: (error) => setReportError(forbiddenMessage(error) ?? apiMessage(error) ?? "Não foi possível gerar o relatório agora."),
  });

  const grouped = inspection.data ? groupBySection(inspection.data.inspection) : null;
  const status = inspection.data ? diffStatusMap(inspection.data.diff) : new Map();
  const evidenceChanges = inspection.data ? evidenceChangeMap(inspection.data.diff) : new Map();

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{ textTransform: "none" }}>Voltar para Atividades</Button>
        {!forbidden && (
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<History />} onClick={() => setHistoryOpen((v) => !v)} sx={{ textTransform: "none" }}>Histórico</Button>
            <Button
              variant="outlined"
              startIcon={report.isPending ? <CircularProgress size={16} color="inherit" /> : <Download />}
              onClick={() => report.mutate()}
              disabled={!latestId || viewingOlder || report.isPending || run.isPending}
              sx={{ textTransform: "none" }}
            >
              Baixar relatório
            </Button>
            <Button variant="contained" startIcon={run.isPending ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />} onClick={() => run.mutate()} disabled={run.isPending} sx={{ textTransform: "none" }}>Rodar Raio-X</Button>
          </Stack>
        )}
      </Stack>

      {viewingOlder && (
        <Typography variant="caption" color="text.secondary">O relatório sai da inspeção mais recente. Volte a ela no Histórico para baixar.</Typography>
      )}

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Raio-X de Configuração</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Inspeciona a configuração deste CNPJ e de cada contrato. Só leitura, nada é alterado.</Typography>

      {forbidden ? (
        <Alert severity="info">{forbidden}</Alert>
      ) : (
        <>
          {runError && <Alert severity="warning" sx={{ mb: 2 }}>{runError}</Alert>}
          {reportError && <Alert severity="warning" sx={{ mb: 2 }}>{reportError}</Alert>}
          {reportNotice && <Alert severity="info" sx={{ mb: 2 }}>{reportNotice}</Alert>}

          <Collapse in={historyOpen}>
            <Box sx={{ mb: 2, border: "1px solid rgba(0,0,0,0.06)", borderRadius: 2, p: 1 }}>
              <RaioXHistory items={history.data ?? []} currentId={effectiveId} isError={history.isError} onSelect={(id) => { setCurrentId(id); setHistoryOpen(false); }} />
            </Box>
          </Collapse>

          {history.isLoading && <CircularProgress size={24} />}
          {history.isError && <Alert severity="error">Não foi possível carregar o histórico de inspeções.</Alert>}

          {!history.isLoading && !history.isError && !effectiveId && (
            <Alert severity="info">Nenhuma inspeção ainda. Clique em "Rodar Raio-X" para inspecionar este CNPJ.</Alert>
          )}

          {inspection.isLoading && effectiveId && <CircularProgress size={24} />}
          {inspection.isError && <Alert severity="error">Não foi possível abrir a inspeção.</Alert>}

          {inspection.data && grouped && (
            <Stack spacing={3}>
              <RaioXSummary inspection={inspection.data.inspection} diff={inspection.data.diff} filters={filters} onFiltersChange={setFilters} workspaceLabels={workspaceLabels} />
              <RaioXSections grouped={grouped} status={status} filters={filters} workspaceLabels={workspaceLabels} evidenceChanges={evidenceChanges} onOpenWorkspace={setOpenWorkspace} />
            </Stack>
          )}
        </>
      )}

      <RaioXWorkspaceDrawer
        open={Boolean(openWorkspace)}
        target={openWorkspace}
        label={openWorkspace ? workspaceLabelOf(openWorkspace.targetId, workspaceLabels) : ""}
        status={status}
        evidenceChanges={evidenceChanges}
        filters={filters}
        workspaceLabels={workspaceLabels}
        onClose={() => setOpenWorkspace(null)}
      />
    </Box>
  );
}
